import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { usePageTransition } from "@/hooks/usePageTransition";
import IntakeTransitionOverlay from "@/components/feature/IntakeTransitionOverlay";
import IntakeProgressBar from "@/components/feature/IntakeProgressBar";
import VSeeClinicWidget from "@/components/feature/VSeeClinicWidget";
import { kLevelBlueprintData, adPanelContent } from "@/mocks/intake-pages";
import {
  getTotalIntakeSteps,
  loadStage1Answers,
} from "@/lib/stage1Questionnaire";
import { fetchStage1KLevel } from "@/lib/klevelApi";
import { startVSeeCall } from "@/lib/vseeApi";
import type { VSeeJoinLinkSession, VSeeSessionConfig } from "@/types/vsee";

type BlueprintState =
  | { status: "loading" }
  | { status: "ready"; mobilitySelection: string; disclaimer: string }
  | { status: "deferred"; reasons: string[] }
  | { status: "insufficient"; flags: string[] }
  | { status: "error" };

export default function IntakeKLevelBlueprint() {
  const { phase, goTo, containerClasses } = usePageTransition();
  const [transitioning, setTransitioning] = useState(false);
  const [state, setState] = useState<BlueprintState>({ status: "loading" });
  const totalSteps = getTotalIntakeSteps();
  const [vseeSession, setVseeSession] = useState<
    VSeeSessionConfig | VSeeJoinLinkSession | null
  >(null);
  const [vseeStarting, setVseeStarting] = useState(false);
  const [vseeError, setVseeError] = useState<string | null>(null);

  const handleCallPhysicianNow = async () => {
    setVseeStarting(true);
    setVseeError(null);
    try {
      setVseeSession(await startVSeeCall());
    } catch (err) {
      setVseeError(
        err instanceof Error ? err.message : "Failed to start the call.",
      );
    } finally {
      setVseeStarting(false);
    }
  };

  useEffect(() => {
    fetchStage1KLevel(loadStage1Answers())
      .then((result) => {
        if (result.outcome === "Deferred") {
          setState({ status: "deferred", reasons: result.deferReasons });
          return;
        }
        if (result.outcome === "InsufficientData") {
          setState({ status: "insufficient", flags: result.flags });
          return;
        }
        if (!result.preliminaryKLevel) {
          setState({ status: "error" });
          return;
        }
        setState({
          status: "ready",
          mobilitySelection: result.preliminaryKLevel.toLowerCase(),
          disclaimer: result.disclaimer,
        });
      })
      .catch(() => setState({ status: "error" }));
  }, []);

  const handleVerifyInsurance = () => {
    setTransitioning(true);
    setTimeout(() => {
      goTo("/intake/account");
    }, 500);
  };

  // Only rendered when state.status === "ready"; always defined so the JSX
  // below doesn't need extra null-checks alongside the state.status check.
  const blueprint =
    state.status === "ready"
      ? kLevelBlueprintData[state.mobilitySelection] || kLevelBlueprintData.k3
      : kLevelBlueprintData.k3;

  return (
    <div className="h-screen flex flex-col bg-background-50">
      <IntakeTransitionOverlay />
      {/* Top Bar: Back + Progress */}
      <div className="flex-shrink-0 bg-white border-b border-background-100">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-6 py-4">
            <Link
              to="/intake/step/F"
              className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              Back
            </Link>
          </div>

          <div className="pb-4">
            <IntakeProgressBar
              currentStep={totalSteps - 3}
              totalSteps={totalSteps}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex items-start justify-center overflow-y-auto px-4 md:px-6 py-8 md:py-12 transition-all duration-500 ease-out relative ${containerClasses}`}
      >
        {/* Warm Background Blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-accent-100/20 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-primary-100/12 blur-3xl"></div>
        </div>

        {state.status === "loading" && (
          <div className="flex items-center justify-center py-24 relative z-10">
            <span className="w-6 h-6 flex items-center justify-center text-primary-500">
              <i className="ri-loader-4-line text-xl animate-spin"></i>
            </span>
          </div>
        )}

        {state.status === "deferred" && (
          <div className="w-full max-w-[600px] mx-auto relative z-10">
            <div className="bg-white rounded-2xl border border-background-100 p-8 md:p-10 text-center animate-fadeSlideIn">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-5">
                <i className="ri-time-line text-xl"></i>
              </span>
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                We need a bit more clinical input first
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                Based on your answers, a licensed physician needs to review the
                following before we can suggest a functional level:
              </p>
              <ul className="text-left space-y-2 mb-6">
                {state.reasons.map((reason) => (
                  <li
                    key={reason}
                    className="flex items-start gap-2.5 p-3 rounded-xl bg-accent-50/50 border border-accent-200 text-xs md:text-sm text-accent-700"
                  >
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-alert-line text-[11px]"></i>
                    </span>
                    {reason}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/intake/step/A"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-primary-500 text-primary-600 text-sm font-bold hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  Review My Answers
                </Link>
                <button
                  type="button"
                  onClick={handleCallPhysicianNow}
                  disabled={vseeStarting}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={
                        vseeStarting
                          ? "ri-loader-4-line text-sm animate-spin"
                          : "ri-phone-line text-sm"
                      }
                    ></i>
                  </span>
                  {vseeStarting ? "Starting call…" : "Talk to a Physician Now"}
                </button>
              </div>
              {vseeError && (
                <p className="text-xs text-red-600 text-center mt-3">
                  {vseeError}
                </p>
              )}
            </div>
          </div>
        )}

        {state.status === "insufficient" && (
          <div className="w-full max-w-[600px] mx-auto relative z-10">
            <div className="bg-white rounded-2xl border border-background-100 p-8 md:p-10 text-center animate-fadeSlideIn">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-5">
                <i className="ri-file-list-3-line text-xl"></i>
              </span>
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                We need a few more self-assessment answers
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                Not enough of the self-assessment section was answered to
                produce a preliminary estimate yet:
              </p>
              <ul className="text-left space-y-2 mb-6">
                {state.flags.map((flag) => (
                  <li
                    key={flag}
                    className="flex items-start gap-2.5 p-3 rounded-xl bg-accent-50/50 border border-accent-200 text-xs md:text-sm text-accent-700"
                  >
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-alert-line text-[11px]"></i>
                    </span>
                    {flag}
                  </li>
                ))}
              </ul>
              <Link
                to="/intake/step/F"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Complete Self-Assessment
              </Link>
            </div>
          </div>
        )}

        {state.status === "error" && (
          <div className="w-full max-w-[600px] mx-auto relative z-10">
            <div className="bg-white rounded-2xl border border-background-100 p-8 md:p-10 text-center animate-fadeSlideIn">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-5">
                <i className="ri-error-warning-line text-xl"></i>
              </span>
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                We couldn&apos;t generate your blueprint
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                Something went wrong while calculating your functional level.
                Please try again in a moment.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {state.status === "ready" && (
          <div className="w-full max-w-[1100px] mx-auto relative z-10">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-stretch">
              {/* Left: Visual Sample Asset */}
              <div className="lg:w-1/2 flex flex-col">
                <div className="bg-white rounded-2xl border border-background-100 p-5 md:p-6 flex flex-col h-full animate-fadeSlideIn">
                  <span className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-3">
                    Custom Component Configuration Preview
                  </span>
                  <div className="flex-1 rounded-xl overflow-hidden bg-foreground-950/5 relative min-h-[360px] md:min-h-[400px]">
                    <img
                      src={blueprint.componentImageUrl}
                      alt={
                        blueprint.kLevel + " prosthetic component configuration"
                      }
                      className="w-full h-full object-contain object-center"
                    />
                  </div>
                  <div className="mt-4 p-4 rounded-xl bg-background-50 border border-background-100">
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      <span className="font-semibold text-foreground-800">
                        Component Summary:
                      </span>{" "}
                      {blueprint.componentSummary}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right: Results Panel & Empowering Copy */}
              <div className="lg:w-1/2 flex flex-col">
                <div className="bg-white rounded-2xl border border-background-100 p-8 md:p-10 flex flex-col h-full animate-slideUpIn">
                  {/* Aspirational Lifestyle Photo — "This is what's possible" */}
                  <div className="mb-8 rounded-xl overflow-hidden relative min-h-[180px] md:min-h-[200px]">
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20natural%20lifestyle%20photograph%20of%20a%20person%20walking%20comfortably%20outdoors%20in%20a%20sunlit%20park%20with%20their%20prosthetic%20leg%2C%20confident%20relaxed%20stride%2C%20wearing%20casual%20modern%20clothing%2C%20golden%20hour%20soft%20natural%20light%2C%20trees%20and%20greenery%20slightly%20blurred%20in%20background%2C%20genuine%20peaceful%20expression%2C%20feeling%20of%20freedom%20and%20normalcy%20restored%2C%20hopeful%20uplifting%20atmosphere%2C%20editorial%20photography%20style%2C%20warm%20cream%20beige%20and%20soft%20green%20tones%2C%20shallow%20depth%20of%20field%20focusing%20on%20the%20walking%20motion%2C%20no%20clinical%20setting%2C%20pure%20outdoor%20lifestyle%20moment&width=800&height=400&seq=klevel-lifestyle-walking&orientation=landscape"
                      alt="Walking comfortably with a prosthetic"
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-[10px] md:text-[11px] font-semibold text-foreground-800">
                        <span className="w-3.5 h-3.5 flex items-center justify-center text-accent-500">
                          <i className="ri-walk-line text-[10px]"></i>
                        </span>
                        This is what we&apos;re working toward
                      </span>
                    </div>
                  </div>

                  {/* K-Level Header */}
                  <div className="mb-6">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-[11px] font-semibold text-accent-800 mb-3">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-bar-chart-2-line text-[10px]"></i>
                      </span>
                      Preliminary Functional Blueprint
                    </span>
                    <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                      Your Preliminary Functional Blueprint: Potential{" "}
                      {blueprint.kLevel} Ambulator
                    </h1>
                    <p className="text-sm text-foreground-500 mt-1 font-medium">
                      {blueprint.kLabel}
                    </p>
                  </div>

                  {/* K-Level Description */}
                  <div className="mb-6">
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      {blueprint.kDescription}
                    </p>
                  </div>

                  {/* Coverage Info */}
                  <div className="mb-6 p-4 rounded-xl bg-primary-50/70 border border-primary-100">
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                        <i className="ri-shield-check-line text-sm"></i>
                      </span>
                      <div>
                        <p className="text-xs md:text-sm font-semibold text-primary-800 mb-1">
                          Coverage Insight
                        </p>
                        <p className="text-xs text-primary-700 leading-relaxed">
                          {blueprint.coverageText}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="mb-8 p-4 rounded-xl border border-accent-200 bg-accent-50/50">
                    <div className="flex items-start gap-2.5">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-accent-600 mt-0.5">
                        <i className="ri-information-line text-sm"></i>
                      </span>
                      <p className="text-[11px] md:text-xs text-accent-700 leading-relaxed">
                        {state.status === "ready" && state.disclaimer}
                      </p>
                    </div>
                  </div>

                  {/* Financial CTA — warm, no dark backgrounds */}
                  <div className="rounded-2xl p-6 md:p-7 text-center mt-auto border border-primary-200 bg-gradient-to-br from-primary-50 via-primary-50/80 to-accent-50/60">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className="ri-shield-star-line text-sm"></i>
                      </span>
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-primary-600">
                        Insurance Coverage Check
                      </span>
                    </div>
                    <p className="text-sm md:text-base font-semibold text-foreground-700 leading-relaxed mb-5">
                      Prosthetics are expensive. Let&apos;s see what your
                      insurance will cover so you won&apos;t have any surprises
                      and you know exactly what to expect.
                    </p>

                    {!transitioning ? (
                      <button
                        type="button"
                        onClick={handleVerifyInsurance}
                        className="w-full py-4 rounded-xl bg-primary-500 text-background-50 text-sm md:text-base font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-file-shield-2-line text-sm"></i>
                        </span>
                        Verify My Insurance Coverage
                      </button>
                    ) : (
                      <div className="flex items-center justify-center gap-2 py-4 text-primary-600 animate-fadeSlideIn">
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-loader-4-line text-sm animate-spin"></i>
                        </span>
                        <span className="text-sm font-semibold">
                          Analyzing coverage options...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {vseeSession &&
        ("joinUrl" in vseeSession ? (
          createPortal(
            <div className="fixed inset-0 z-[999] bg-foreground-950 flex flex-col">
              <div className="flex-shrink-0 flex items-center justify-end px-4 py-2.5 bg-foreground-900">
                <button
                  type="button"
                  onClick={() => setVseeSession(null)}
                  aria-label="Close"
                  className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-base"></i>
                </button>
              </div>
              <iframe
                src={vseeSession.joinUrl}
                className="flex-1 min-h-0"
                allow="camera; microphone; fullscreen; display-capture"
              />
            </div>,
            document.body,
          )
        ) : (
          <VSeeClinicWidget
            session={vseeSession}
            onLeave={() => setVseeSession(null)}
          />
        ))}

      {/* Compliance Footer */}
      <div className="w-full border-t border-background-100 bg-background-50">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
              <i className="ri-information-line text-[10px]"></i>
            </span>
            <p className="text-[10px] text-foreground-400 leading-relaxed max-w-3xl">
              Notice: This platform is an administrative optimization tool. All
              AI-generated functional assessments, financial forecasts,
              wholesale cost aggregations, advertising recommendations, 3D
              metric visualizations, and documentation templates are strictly
              informational. Final clinical specifications, billing submittals,
              prescriptions, and custom fabrication workflows require formal,
              independent review and explicit sign-off by a licensed physician
              and a Certified Prosthetist prior to patient care.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
